import { useState } from 'react';
import { Client, Vacancy, CopyVariant, Visual } from '../../consultantTypes';
import { ClientModal } from '../components/ClientModal';
import { VacancyModal } from '../components/VacancyModal';
import { Plus, Briefcase, ChevronRight, CheckCircle2, AlertCircle, Clock, File as FileEdit, Link2, Check } from 'lucide-react';

interface ConsultantDashboardProps {
  clients: Client[];
  vacancies: Vacancy[];
  copyVariants: CopyVariant[];
  visuals: Visual[];
  onAddClient: (name: string) => void;
  onAddVacancy: (clientId: string, vacancy: { title: string; vacancyText: string; notes: string }) => void;
  onOpenComposer: (clientId: string, vacancyId: string) => void;
}

export function ConsultantDashboard({
  clients,
  vacancies,
  copyVariants,
  visuals,
  onAddClient,
  onAddVacancy,
  onOpenComposer
}: ConsultantDashboardProps) {
  const [copiedVacancyId, setCopiedVacancyId] = useState<string | null>(null);

  const copyShareLink = async (vacancy: Vacancy) => {
    if (!vacancy.shareToken) return;

    const shareUrl = `${window.location.origin}/client?share=${vacancy.shareToken}`;

    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopiedVacancyId(vacancy.id);
      setTimeout(() => setCopiedVacancyId(null), 2000);
    } catch (err) {
      console.error('Failed to copy link:', err);
      alert('Kon link niet kopiëren. Probeer het opnieuw.');
    }
  };

  const getVacancyStatus = (vacancy: Vacancy) => {
    if (!vacancy.shared) {
      return { type: 'draft', label: 'Draft', icon: FileEdit, color: 'blue' };
    }

    if (!vacancy.reviewCompletedAt) {
      return { type: 'pending', label: 'In afwachting', icon: Clock, color: 'gray' };
    }

    const vacancyCopyVariants = copyVariants.filter(cv => cv.vacancyId === vacancy.id && cv.selected);
    const vacancyVisuals = visuals.filter(v => v.vacancyId === vacancy.id && v.selected);

    const allCopyApproved = vacancyCopyVariants.length > 0 && vacancyCopyVariants.every(cv => cv.status === 'approved');
    const allVisualsApproved = vacancyVisuals.length > 0 && vacancyVisuals.every(v => v.status === 'APPROVED');

    if (allCopyApproved && allVisualsApproved) {
      return { type: 'approved', label: 'Goedgekeurd', icon: CheckCircle2, color: 'green' };
    }

    return { type: 'feedback', label: 'Feedback ontvangen', icon: AlertCircle, color: 'orange' };
  };
  const [selectedClientId, setSelectedClientId] = useState<string>('');
  const [selectedVacancyId, setSelectedVacancyId] = useState<string>('');
  const [showClientModal, setShowClientModal] = useState(false);
  const [showVacancyModal, setShowVacancyModal] = useState(false);

  const filteredVacancies = selectedClientId
    ? vacancies.filter(v => v.clientId === selectedClientId)
    : vacancies;

  const selectedClient = clients.find(c => c.id === selectedClientId);
  const selectedVacancy = vacancies.find(v => v.id === selectedVacancyId);

  const handleAddVacancy = (data: { title: string; vacancyText: string; notes: string }) => {
    if (selectedClientId) {
      onAddVacancy(selectedClientId, data);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Briefcase className="w-8 h-8 text-gray-700" />
            <h1 className="text-3xl font-bold text-gray-900">Consultant Dashboard</h1>
          </div>
          <p className="text-gray-600">Beheer klanten, vacatures en campagne content</p>
        </div>

        <div className="bg-white rounded-2xl shadow-md p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Klant
              </label>
              <div className="flex gap-2">
                <select
                  value={selectedClientId}
                  onChange={(e) => {
                    setSelectedClientId(e.target.value);
                    setSelectedVacancyId('');
                  }}
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Selecteer een klant...</option>
                  {clients.map(client => (
                    <option key={client.id} value={client.id}>
                      {client.name}
                    </option>
                  ))}
                </select>
                <button
                  onClick={() => setShowClientModal(true)}
                  className="px-4 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                  aria-label="Nieuwe klant toevoegen"
                >
                  <Plus className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Vacature
              </label>
              <div className="flex gap-2">
                <select
                  value={selectedVacancyId}
                  onChange={(e) => setSelectedVacancyId(e.target.value)}
                  disabled={!selectedClientId}
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                >
                  <option value="">Selecteer een vacature...</option>
                  {filteredVacancies.map(vacancy => (
                    <option key={vacancy.id} value={vacancy.id}>
                      {vacancy.title}
                    </option>
                  ))}
                </select>
                <button
                  onClick={() => setShowVacancyModal(true)}
                  disabled={!selectedClientId}
                  className="px-4 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed"
                  aria-label="Nieuwe vacature toevoegen"
                >
                  <Plus className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>

          {selectedClient && selectedVacancy && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-gray-900">{selectedVacancy.title}</h3>
                  <p className="text-sm text-gray-600 mt-1">{selectedClient.name}</p>
                  {selectedVacancy.shared && (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-200 mt-2">
                      Gedeeld met klant
                    </span>
                  )}
                </div>
                <button
                  onClick={() => onOpenComposer(selectedClientId, selectedVacancyId)}
                  className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Open Composer
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="bg-white rounded-2xl shadow-md p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            {selectedClientId ? `Vacatures voor ${selectedClient?.name}` : 'Alle Vacatures'}
          </h2>
          {filteredVacancies.length > 0 ? (
            <div className="space-y-3">
              {filteredVacancies.map(vacancy => {
                const vacancyClient = clients.find(c => c.id === vacancy.clientId);
                return (
                <div
                  key={vacancy.id}
                  className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="flex-1">
                    <h3 className="font-medium text-gray-900">{vacancy.title}</h3>
                    {!selectedClientId && vacancyClient && (
                      <p className="text-sm text-gray-600 mt-1">{vacancyClient.name}</p>
                    )}
                    <div className="flex items-center gap-2 mt-2">
                      {(() => {
                        const status = getVacancyStatus(vacancy);
                        const StatusIcon = status.icon;
                        return (
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                            status.color === 'green' ? 'bg-green-50 text-green-700 border border-green-200' :
                            status.color === 'orange' ? 'bg-orange-50 text-orange-700 border border-orange-200' :
                            status.color === 'blue' ? 'bg-blue-50 text-blue-700 border border-blue-200' :
                            'bg-gray-50 text-gray-700 border border-gray-200'
                          }`}>
                            <StatusIcon className="w-3 h-3" />
                            {status.label}
                          </span>
                        );
                      })()}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => copyShareLink(vacancy)}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg font-medium text-sm transition-colors ${
                        copiedVacancyId === vacancy.id
                          ? 'bg-green-100 text-green-700'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                      title="Kopieer link voor klant"
                    >
                      {copiedVacancyId === vacancy.id ? (
                        <>
                          <Check className="w-4 h-4" />
                          Gekopieerd!
                        </>
                      ) : (
                        <>
                          <Link2 className="w-4 h-4" />
                          Kopieer Link
                        </>
                      )}
                    </button>
                    <button
                      onClick={() => {
                        setSelectedVacancyId(vacancy.id);
                        onOpenComposer(vacancy.clientId, vacancy.id);
                      }}
                      className="text-sm font-medium text-blue-600 hover:text-blue-700 px-3 py-2"
                    >
                      Bewerken
                    </button>
                  </div>
                </div>
              );
              })}
            </div>
          ) : (
            <div className="text-center py-8">
              <Briefcase className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-500">
                {selectedClientId
                  ? 'Nog geen vacatures voor deze klant'
                  : 'Nog geen vacatures aangemaakt'}
              </p>
              <p className="text-sm text-gray-400 mt-1">
                Selecteer een klant en klik op + om een vacature toe te voegen
              </p>
            </div>
          )}
        </div>
      </div>

      {showClientModal && (
        <ClientModal
          onClose={() => setShowClientModal(false)}
          onSave={onAddClient}
        />
      )}

      {showVacancyModal && selectedClientId && (
        <VacancyModal
          clientId={selectedClientId}
          onClose={() => setShowVacancyModal(false)}
          onSave={handleAddVacancy}
        />
      )}
    </div>
  );
}
